import { defaultCreateBackend, supportsAutoplayBlocking } from './default-audio-backend'
import { withoutActWarning } from '@w/utils-fe'
import { isTest } from '@w/utils'

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min
  if (value > max) return max
  return value
}

interface BaseSnapshot {
  volume: number
  progress: number
  duration: number
  url?: string
}

export type AudioControllerState =
  | ({ state: 'idle' } & BaseSnapshot)
  | ({ state: 'loading' } & BaseSnapshot)
  | ({ state: 'ready' } & BaseSnapshot)
  | ({ state: 'playing' } & BaseSnapshot)
  | ({ state: 'paused' } & BaseSnapshot)
  | ({ state: 'ended' } & BaseSnapshot)
  | ({ state: 'blocked'; error: string } & BaseSnapshot)
  | ({ state: 'error'; error: string } & BaseSnapshot)

function snapshotsEqual(
  a: AudioControllerState,
  b: AudioControllerState,
): boolean {
  if (a.state !== b.state) return false
  if (a.volume !== b.volume) return false
  if (a.progress !== b.progress) return false
  if (a.duration !== b.duration) return false

  const aHasUrl = 'url' in a
  const bHasUrl = 'url' in b
  if (aHasUrl || bHasUrl) {
    if (!(aHasUrl && bHasUrl)) return false
    if (a.url !== b.url) return false
  }

  const aHasError = 'error' in a
  const bHasError = 'error' in b
  if (aHasError || bHasError) {
    if (!(aHasError && bHasError)) return false
    if (a.error !== b.error) return false
  }

  return true
}

export interface AudioController {
  getSnapshot(): AudioControllerState
  subscribe(listener: () => void): () => void
  load(source?: AudioSource | null, loadOptions?: LoadOptions): void
  play(): Promise<void>
  pause(): void
  seekTo(ms: number): void
  seekBy(ms: number): void
  setVolume(volume: number): void
  setOnEnd(handler: (() => void) | undefined): void
  dispose(): void
}

export interface AudioAsset {
  url: string
  buffer?: AudioBuffer
  durationMs: number
}

export interface PlaybackHandle {
  stop(): void
  startedAtMs: number
}

export interface AudioBackend {
  load(url: string, signal: AbortSignal): Promise<AudioAsset>
  decode(buffer: ArrayBuffer): Promise<AudioAsset>
  play(
    asset: AudioAsset,
    offsetSeconds: number,
    onEnded: () => void,
  ): Promise<PlaybackHandle>
  stop(): void
  getCurrentTimeMs(): number
  setVolume(volume: number): void
  setOnError?(handler: (message: string) => void): void
  close(): void
}

interface LoadOptions {
  initialProgressMs?: number
  initialVolume?: number
  autoplay?: boolean
}

type AudioSource =
  | { kind: 'url'; url: string }
  | { kind: 'arrayBuffer'; arrayBuffer: ArrayBuffer }

export interface CreateAudioControllerOptions {
  createBackend?: () => AudioBackend
  tickIntervalMs?: number
}

export function createAudioController(
  options?: CreateAudioControllerOptions,
): AudioController {
  const createBackend = options?.createBackend ?? defaultCreateBackend
  const tickIntervalMs = options?.tickIntervalMs ?? 100
  const hasAnimationFrame =
    options?.tickIntervalMs === undefined &&
    typeof globalThis.requestAnimationFrame === 'function' &&
    typeof globalThis.cancelAnimationFrame === 'function'
  const requestFrame = hasAnimationFrame
    ? globalThis.requestAnimationFrame.bind(globalThis)
    : undefined
  const cancelFrame = hasAnimationFrame
    ? globalThis.cancelAnimationFrame.bind(globalThis)
    : undefined

  const backend = createBackend()

  let snapshot: AudioControllerState = {
    state: 'idle',
    volume: 1,
    progress: 0,
    duration: 0,
  }

  const listeners = new Set<() => void>()

  let stateName: AudioControllerState['state'] = 'idle'
  let currentUrl: string | undefined
  let currentAsset: AudioAsset | null = null
  let loadAbort: AbortController | null = null
  let loadToken: symbol | null = null
  let playbackHandle: PlaybackHandle | null = null
  let playbackToken: symbol | null = null
  let playbackOffsetMs = 0
  let progressMs = 0
  let durationMs = 0
  let volume = 1
  let errorMessage: string | undefined
  let pendingPlay = false
  let onEndHandler: (() => void) | undefined
  let cancelTick: (() => void) | null = null

  const notify = (state: AudioControllerState) => {
    // Snapshot is controller state, not a React mutation. Publish it
    // synchronously so imperative readers never depend on a later test act
    // flush. Only subscribed React listeners need the act boundary.
    snapshot = state
    if (listeners.size === 0) return

    withoutActWarning(() => {
      for (const listener of Array.from(listeners)) {
        listener()
      }
    })
  }

  const buildSnapshot = (): AudioControllerState => {
    const base = {
      volume,
      progress: progressMs,
      duration: durationMs,
      ...(currentUrl ? { url: currentUrl } : {}),
    }

    if (stateName === 'error' || stateName === 'blocked') {
      return {
        state: stateName,
        error: errorMessage ?? 'Unknown error',
        ...base,
      }
    }

    return {
      state: stateName,
      ...base,
    }
  }

  const publish = () => {
    const next = buildSnapshot()
    if (snapshotsEqual(snapshot, next)) {
      return
    }
    notify(next)
  }

  const stopTick = () => {
    if (!cancelTick) {
      return
    }
    cancelTick()
    cancelTick = null
  }

  const startTick = () => {
    if (cancelTick) {
      return
    }

    if (hasAnimationFrame && requestFrame && cancelFrame) {
      let frameId = 0
      const step = () => {
        syncProgress()
        frameId = requestFrame(step)
      }
      frameId = requestFrame(step)
      cancelTick = () => {
        cancelFrame(frameId)
      }
      return
    }

    const intervalId: ReturnType<typeof setInterval> = setInterval(
      () => {
        syncProgress()
      },
      isTest ? 0 : tickIntervalMs,
    )
    cancelTick = () => {
      clearInterval(intervalId)
    }
  }

  const stopPlayback = () => {
    playbackToken = null
    stopTick()
    if (playbackHandle) {
      playbackHandle.stop()
      playbackHandle = null
    }
    backend.stop()
  }

  const resetProgress = () => {
    playbackOffsetMs = 0
    progressMs = 0
  }

  const resetPlaybackState = ({
    resetUrl,
    closeBackend,
  }: {
    resetUrl: boolean
    closeBackend: boolean
  }) => {
    pendingPlay = false
    if (loadAbort) {
      loadAbort.abort()
      loadAbort = null
    }
    loadToken = null
    stopPlayback()
    currentAsset = null
    resetProgress()
    durationMs = 0
    errorMessage = undefined
    if (resetUrl) {
      currentUrl = undefined
    }
    if (closeBackend) {
      backend.close()
    }
  }

  const setError = (message: string) => {
    resetPlaybackState({ resetUrl: false, closeBackend: false })
    errorMessage = message
    stateName = 'error'
    publish()
  }

  backend.setOnError?.(setError)

  const handlePlaybackEnded = (token: symbol | null) => {
    if (!currentAsset || token === null || playbackToken !== token) {
      return
    }

    stopTick()
    playbackHandle = null
    playbackToken = null
    playbackOffsetMs = durationMs
    progressMs = durationMs
    stateName = 'ended'
    publish()

    if (onEndHandler) {
      onEndHandler()
    }
  }

  const syncProgress = () => {
    if (!currentAsset) {
      return
    }

    if (playbackHandle) {
      const elapsed = Math.max(
        0,
        backend.getCurrentTimeMs() - playbackHandle.startedAtMs,
      )
      const computed = Math.min(playbackOffsetMs + elapsed, durationMs)

      if (computed !== progressMs) {
        progressMs = computed
        publish()
      }

      if (computed >= durationMs) {
        handlePlaybackEnded(playbackToken)
      }
      return
    }

    if (progressMs > durationMs) {
      progressMs = durationMs
      publish()
    }
  }

  const beginPlayback = async (autoplay = false) => {
    if (!currentAsset) {
      return
    }

    const token = Symbol('playback')
    playbackToken = token

    try {
      const handle = await backend.play(
        currentAsset,
        playbackOffsetMs / 1000,
        () => {
          handlePlaybackEnded(token)
        },
      )

      if (playbackToken !== token) {
        handle.stop()
        return
      }

      playbackHandle = handle
      stateName = 'playing'
      publish()
      startTick()
    } catch (err) {
      if (playbackToken === token) {
        const message = err instanceof Error ? err.message : String(err)
        if (autoplay && supportsAutoplayBlocking) {
          errorMessage = message
          stateName = 'blocked'
          publish()
        } else {
          setError(message)
        }
      }
    }
  }

  const load = (source?: AudioSource | null, loadOptions?: LoadOptions) => {
    if (!source) {
      resetPlaybackState({ resetUrl: true, closeBackend: false })
      stateName = 'idle'
      publish()
      return
    }

    resetPlaybackState({ resetUrl: false, closeBackend: false })

    currentUrl = source.kind === 'url' ? source.url : undefined
    pendingPlay = !!loadOptions?.autoplay

    if (typeof loadOptions?.initialVolume === 'number') {
      volume = clamp(loadOptions.initialVolume, 0, 1)
      backend.setVolume(volume)
    }

    const abortController = source.kind === 'url' ? new AbortController() : null
    loadAbort = abortController
    const token = Symbol('load')
    loadToken = token

    stateName = 'loading'
    publish()
    void (async () => {
      try {
        const asset =
          source.kind === 'url'
            ? // oxlint-disable-next-line typescript/no-non-null-assertion -- fine while `source.kind === 'url'` checks it
              await backend.load(source.url, abortController!.signal)
            : await backend.decode(source.arrayBuffer)

        if (loadToken !== token) {
          return
        }

        currentAsset = asset
        durationMs = asset.durationMs
        if (typeof loadOptions?.initialProgressMs === 'number') {
          const clamped = clamp(loadOptions.initialProgressMs, 0, durationMs)
          progressMs = clamped
          playbackOffsetMs = clamped
          if (clamped >= durationMs) {
            stateName = 'ended'
          } else if (clamped > 0) {
            stateName = 'paused'
          } else {
            stateName = 'ready'
          }
        } else {
          progressMs = 0
          playbackOffsetMs = 0
          stateName = 'ready'
        }
        publish()

        if (pendingPlay && stateName !== 'ended') {
          pendingPlay = false
          await beginPlayback(true)
        }
      } catch (err) {
        if (loadToken !== token) {
          return
        }

        const message = err instanceof Error ? err.message : String(err)
        setError(message)
      } finally {
        if (loadToken === token) {
          loadToken = null
          loadAbort = null
        }
      }
    })()
  }

  const play = async () => {
    if (stateName === 'loading') {
      pendingPlay = true
      return
    }

    if (!currentAsset) {
      return
    }

    if (stateName === 'ended') {
      playbackOffsetMs = 0
      progressMs = 0
    }

    playbackOffsetMs = clamp(progressMs, 0, durationMs)
    progressMs = playbackOffsetMs

    await beginPlayback()
  }

  const pause = () => {
    if (stateName !== 'playing') {
      return
    }

    syncProgress()
    playbackOffsetMs = progressMs
    stopPlayback()
    stateName = 'paused'
    publish()
  }

  const seekTo = (ms: number) => {
    if (!currentAsset) {
      return
    }

    const clamped = clamp(ms, 0, durationMs)

    if (
      progressMs === clamped &&
      playbackOffsetMs === clamped &&
      stateName !== 'ended'
    ) {
      return
    }

    progressMs = clamped
    playbackOffsetMs = clamped

    if (stateName === 'playing') {
      publish()
      stopPlayback()
      void beginPlayback()
      return
    }

    if (stateName === 'ended' && clamped < durationMs) {
      stateName = 'paused'
    }

    publish()
  }

  const seekBy = (delta: number) => {
    seekTo(progressMs + delta)
  }

  const setVolumeHandler = (value: number) => {
    const clamped = clamp(value, 0, 1)
    if (clamped === volume) {
      return
    }

    volume = clamped
    backend.setVolume(clamped)
    publish()
  }

  const setOnEnd = (handler: (() => void) | undefined) => {
    onEndHandler = handler
  }

  const dispose = () => {
    resetPlaybackState({ resetUrl: true, closeBackend: true })
    stateName = 'idle'
    publish()
  }

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    load,
    play,
    pause,
    seekTo,
    seekBy,
    setVolume: setVolumeHandler,
    setOnEnd,
    dispose,
  }
}
